import { notify } from '@affine/component';
import { SignInPanel } from '@affine/core/components/sign-in';
import * as styles from '@affine/core/components/sign-in/style.css';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { invitationReturnPath } from '@affine/core/modules/cloud/invitation';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  RouteLogic,
  useNavigateHelper,
} from '../../../components/hooks/use-navigate-helper';

export const SignIn = ({
  redirectUrl: redirectUrlFromProps,
}: {
  redirectUrl?: string;
}) => {
  const t = useI18n();
  const navigate = useNavigate();
  const { jumpToIndex } = useNavigateHelper();
  const [searchParams] = useSearchParams();
  const setup = searchParams.get('setup') === 'true';
  const forgotPassword = searchParams.get('forgotPassword') === 'true';
  const requestedRedirect =
    redirectUrlFromProps ?? searchParams.get('redirect_uri');
  const redirectUrl =
    setup || forgotPassword
      ? invitationReturnPath(
          requestedRedirect ?? undefined,
          window.location.origin
        )
      : requestedRedirect;

  const server = searchParams.get('server') ?? undefined;
  const error = searchParams.get('error');

  useEffect(() => {
    if (error) {
      notify.error({
        title: t['com.affine.auth.toast.title.failed'](),
        message: error,
      });
    }
  }, [error, t]);

  const handleClose = useCallback(() => {
    jumpToIndex(RouteLogic.REPLACE, {
      search: searchParams.toString(),
    });
  }, [jumpToIndex, searchParams]);

  const handleAuthenticated = useCallback(
    (status: AuthSessionStatus) => {
      if (status === 'authenticated') {
        if (redirectUrl) {
          if (redirectUrl.toUpperCase() === 'CLOSE_POPUP') {
            window.close();
          }
          Promise.resolve(
            navigate(redirectUrl === '/?initCloud=true' ? '/' : redirectUrl, {
              replace: true,
            })
          ).catch(console.error);
        } else {
          handleClose();
        }
      }
    },
    [handleClose, navigate, redirectUrl]
  );

  const initStep = server ? 'addSelfhosted' : 'signIn';

  const handleSkip = useCallback(() => {
    Promise.resolve(navigate('/', { replace: true })).catch(console.error);
  }, [navigate]);

  return (
    <div className={styles.signInCard}>
      {searchParams.get('passwordReset') === 'true' && (
        <p role="status">Password updated. Sign in with your new password.</p>
      )}
      <SignInPanel
        onSkip={handleSkip}
        onAuthenticated={handleAuthenticated}
        initStep={initStep}
        initialEmail={searchParams.get('email') ?? undefined}
        initialSetup={setup}
        initialForgotPassword={forgotPassword}
        requirePasswordSignIn={searchParams.get('passwordReset') === 'true'}
        initialToken={searchParams.get('token') ?? undefined}
        server={server}
        redirectUrl={redirectUrl ?? undefined}
      />
    </div>
  );
};

export const Component = () => {
  return (
    <div className={styles.signInPage}>
      <SignIn />
    </div>
  );
};
